import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from '@tanstack/react-router';
import {
  PlatformRole,
  platformRoleAtLeast,
  updateProfileSchema,
  USER_PERSONAS,
  type OrganizationSettings,
  type OrgRole,
  type UpdateProfileInput,
  type UserPersona,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import Form from '@carbon/react/es/components/Form/Form';
import { Stack } from '@carbon/react/es/components/Stack/Stack';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Tag from '@carbon/react/es/components/Tag/Tag';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import RadioButtonGroup from '@carbon/react/es/components/RadioButtonGroup/RadioButtonGroup';
import RadioButton from '@carbon/react/es/components/RadioButton/RadioButton';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { useThemeStore, type ThemeMode } from '../theme/store.js';
import { useT, type MessageKey } from '../i18n/index.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { PageHeader } from '../components/PageHeader.js';

/** Libellés de rôle, côté utilisateur (identifiants en anglais, affichage en français). */
const ROLE_LABEL: Record<OrgRole, string> = {
  VIEWER: 'Lecteur',
  ANNOTATOR: 'Annotateur',
  EDITOR: 'Éditeur',
  ADMIN: 'Administrateur',
  OWNER: 'Propriétaire',
};

const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

export function ProfilePage() {
  const currentUser = useAuthStore((state) => state.currentUser);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      displayName: currentUser?.displayName ?? '',
      email: currentUser?.email ?? '',
    },
  });

  if (!currentUser) {
    return null;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setSaved(false);
    try {
      await updateProfile(values);
      setSaved(true);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Enregistrement impossible.');
    }
  });

  return (
    <section className="ddp-page__section ddp-profile">
      <PageHeader title="Profil" lead="Vos informations de compte et vos accès par organisation." />

      <Form onSubmit={onSubmit}>
        <Stack gap={6}>
          {saved && (
            <InlineNotification
              kind="success"
              title="Profil mis à jour"
              lowContrast
              hideCloseButton
            />
          )}
          {formError && (
            <InlineNotification
              kind="error"
              title="Échec de l’enregistrement"
              subtitle={formError}
              lowContrast
              hideCloseButton
            />
          )}

          <TextInput
            id="displayName"
            labelText="Nom affiché"
            invalid={Boolean(errors.displayName)}
            invalidText={errors.displayName?.message}
            {...register('displayName')}
          />
          <TextInput
            id="email"
            type="email"
            labelText="Adresse e-mail"
            autoComplete="email"
            invalid={Boolean(errors.email)}
            invalidText={errors.email?.message}
            {...register('email')}
          />

          <Button type="submit" disabled={isSubmitting || !isDirty}>
            Enregistrer
          </Button>
        </Stack>
      </Form>

      <dl className="ddp-profile__facts">
        <div>
          <dt>Membre depuis</dt>
          <dd>{formatDate(currentUser.createdAt)}</dd>
        </div>
        <div>
          <dt>Double authentification</dt>
          <dd>{currentUser.totpEnabled ? 'Activée' : 'Désactivée'}</dd>
        </div>
      </dl>

      <h2 className="ddp-profile__subtitle">Organisations</h2>
      <ul className="ddp-profile__orgs">
        {currentUser.memberships.map((membership) => (
          <li key={membership.organizationId} className="ddp-profile__org">
            <span className="ddp-profile__org-name">{membership.organizationName}</span>
            <Tag type="blue" size="sm">
              {ROLE_LABEL[membership.role]}
            </Tag>
          </li>
        ))}
      </ul>

      <SettingsSection
        canEdit={
          currentUser.memberships[0]
            ? ROLE_RANK[currentUser.memberships[0].role] >= ROLE_RANK.ADMIN
            : false
        }
        canAdminConsole={platformRoleAtLeast(currentUser.platformRole, PlatformRole.SUPPORT)}
      />
    </section>
  );
}

/**
 * Paramètres de l'organisation. La rétention de la corbeille est modifiable par un administrateur ;
 * tout le monde peut consulter le réglage et accéder à la corbeille.
 */
function SettingsSection({
  canEdit,
  canAdminConsole,
}: {
  canEdit: boolean;
  canAdminConsole: boolean;
}) {
  const [retentionDays, setRetentionDays] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch<OrganizationSettings>('/organizations/current/settings')
      .then((s) => {
        if (active) {
          setRetentionDays(String(s.retentionDays));
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const onSave = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const days = Number.parseInt(retentionDays, 10);
      const updated = await apiFetch<OrganizationSettings>('/organizations/current/settings', {
        method: 'PATCH',
        body: { retentionDays: days },
      });
      setRetentionDays(String(updated.retentionDays));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 className="ddp-profile__subtitle">Paramètres</h2>
      <PersonaSetting />
      <ApproxLocationSetting />
      <ThemeSetting />
      {canAdminConsole && (
        <div className="ddp-profile__setting">
          <p>
            <RouterLink to="/admin">Console d’administration</RouterLink> — vue transverse de la
            plateforme (toutes les organisations), réservée aux exploitants de la plateforme.
          </p>
        </div>
      )}
      <div className="ddp-profile__setting">
        <p>
          <RouterLink to="/organisation/membres">Membres</RouterLink> — les personnes de
          l’organisation, leurs rôles et les invitations{canEdit ? '' : ' (consultation)'}.
        </p>
      </div>
      {canEdit && (
        <div className="ddp-profile__setting">
          <p>
            <RouterLink to="/organisation/admin">Administration de l’organisation</RouterLink> —
            l’état des services de l’architecture (et, à terme, usages et coûts), réservée aux
            administrateurs.
          </p>
        </div>
      )}
      <div className="ddp-profile__setting">
        <p>
          <RouterLink to="/corbeille">Corbeille</RouterLink> — restaurer ou purger les vidéos et
          images supprimées.
        </p>
      </div>
      {loaded && (
        <div className="ddp-profile__setting">
          <TextInput
            id="retention-days"
            type="number"
            min={7}
            max={1825}
            labelText="Rétention en corbeille (jours avant purge définitive)"
            helperText="Un élément supprimé reste restaurable pendant cette durée."
            value={retentionDays}
            readOnly={!canEdit}
            onChange={(e) => setRetentionDays(e.target.value)}
          />
          {canEdit && (
            <Button size="sm" disabled={busy} onClick={() => void onSave()}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          )}
          {saved && (
            <InlineNotification
              kind="success"
              title="Paramètres enregistrés"
              lowContrast
              hideCloseButton
            />
          )}
          {error && (
            <InlineNotification
              kind="error"
              title="Échec"
              subtitle={error}
              lowContrast
              hideCloseButton
            />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Choix du **persona d'interface** (ADR-0031) : préférence d'affichage (onglet d'accueil + visites
 * guidées proposées), **jamais un droit**. Persistée côté serveur (contrairement au thème, local) via
 * `updateOnboarding`. Les libellés viennent des mêmes clés i18n que la modale de première connexion.
 */
function PersonaSetting() {
  const t = useT();
  const currentUser = useAuthStore((state) => state.currentUser);
  const updateOnboarding = useAuthStore((state) => state.updateOnboarding);
  const [error, setError] = useState<string | null>(null);
  // Repli d'affichage sur GENERALIST tant que rien n'est choisi (la valeur en base reste `null`).
  const current: UserPersona = currentUser?.persona ?? 'GENERALIST';

  const onChange = async (persona: UserPersona) => {
    setError(null);
    try {
      await updateOnboarding({ persona });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    }
  };

  return (
    <div className="ddp-profile__setting">
      <RadioButtonGroup
        legendText="Profil d’utilisation"
        name="user-persona"
        valueSelected={current}
        orientation="vertical"
        onChange={(value) => void onChange(value as UserPersona)}
      >
        {USER_PERSONAS.map((persona) => (
          <RadioButton
            key={persona}
            labelText={t(`persona.${persona}.label` as MessageKey)}
            value={persona}
            id={`persona-${persona}`}
          />
        ))}
      </RadioButtonGroup>
      <p className="ddp-profile__hint">
        Adapte votre onglet d’accueil et les visites guidées proposées. Rien n’est masqué : toute la
        navigation reste accessible. Ce choix n’a aucun effet sur vos droits.
      </p>
      {error && (
        <InlineNotification
          kind="error"
          title="Échec"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
    </div>
  );
}

/**
 * Localisation **approximative** déclarée (ADR-0040, C6) : sert de **repli** pour centrer la carte
 * opérationnelle quand la géolocalisation du navigateur est indisponible ou refusée. Volontairement
 * grossière (pas de suivi en direct) ; posée par l'utilisateur, persistée via `updateProfile`.
 */
function ApproxLocationSetting() {
  const currentUser = useAuthStore((state) => state.currentUser);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const [lat, setLat] = useState(
    currentUser?.approxLat != null ? String(currentUser.approxLat) : '',
  );
  const [lon, setLon] = useState(
    currentUser?.approxLon != null ? String(currentUser.approxLon) : '',
  );
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locate = () => {
    setError(null);
    if (!('geolocation' in navigator)) {
      setError('La géolocalisation n’est pas disponible sur cet appareil.');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(5));
        setLon(pos.coords.longitude.toFixed(5));
        setBusy(false);
      },
      () => {
        setError('Position indisponible. Saisissez-la à la main.');
        setBusy(false);
      },
      // Approximative suffit : pas de GPS haute précision, et une position récente en cache convient.
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 },
    );
  };

  const save = async () => {
    setError(null);
    setSaved(false);
    const latN = lat.trim() === '' ? null : Number(lat);
    const lonN = lon.trim() === '' ? null : Number(lon);
    if (
      (latN !== null && (!Number.isFinite(latN) || latN < -90 || latN > 90)) ||
      (lonN !== null && (!Number.isFinite(lonN) || lonN < -180 || lonN > 180))
    ) {
      setError('Coordonnées invalides.');
      return;
    }
    setBusy(true);
    try {
      await updateProfile({ approxLat: latN, approxLon: lonN });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ddp-profile__setting">
      <p className="ddp-profile__setting-label">Localisation approximative</p>
      <div className="ddp-profile__geo">
        <TextInput
          id="approx-lat"
          type="number"
          labelText="Latitude"
          value={lat}
          disabled={busy}
          onChange={(e) => setLat(e.target.value)}
        />
        <TextInput
          id="approx-lon"
          type="number"
          labelText="Longitude"
          value={lon}
          disabled={busy}
          onChange={(e) => setLon(e.target.value)}
        />
      </div>
      <div className="ddp-profile__geo-actions">
        <Button kind="tertiary" size="sm" disabled={busy} onClick={locate}>
          Utiliser ma position actuelle
        </Button>
        <Button size="sm" disabled={busy} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
      <p className="ddp-profile__hint">
        Centre la carte opérationnelle quand la géolocalisation du navigateur est refusée ou
        indisponible. Volontairement approximative — aucun suivi en direct.
      </p>
      {saved && (
        <InlineNotification kind="success" title="Enregistré" lowContrast hideCloseButton />
      )}
      {error && (
        <InlineNotification
          kind="error"
          title="Échec"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
    </div>
  );
}

/**
 * Choix du thème (nuit par défaut / jour). Préférence personnelle, locale à l'appareil : offerte à
 * tous les rôles, sans appel réseau — le store pose l'attribut de thème et mémorise le choix.
 */
function ThemeSetting() {
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);

  return (
    <div className="ddp-profile__setting">
      <RadioButtonGroup
        legendText="Thème"
        name="theme-mode"
        valueSelected={mode}
        onChange={(value) => setMode(value as ThemeMode)}
      >
        <RadioButton labelText="Nuit (par défaut)" value="dark" id="theme-dark" />
        <RadioButton labelText="Jour" value="light" id="theme-light" />
      </RadioButtonGroup>
      <p className="ddp-profile__hint">
        Le thème nuit convient à l’examen d’images aériennes ; le thème jour à l’annotation en
        extérieur, en pleine lumière.
      </p>
    </div>
  );
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
